import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Eye, CreditCard, Ticket } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getStatusLabel, getStatusColor, formatPrice, getOperationLabel } from '@/utils/formatters';
import { toast } from 'sonner';

const AdminPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Acceso denegado');
      return;
    }
    fetchProperties();
  }, [isAdmin, statusFilter]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (statusFilter) params.status = statusFilter;

      const { data } = await axiosInstance.get('/admin/properties', { params });
      setProperties(data.properties);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (propertyId, newStatus) => {
    try {
      await axiosInstance.patch(`/admin/properties/${propertyId}/status`, {
        status: newStatus,
      });
      toast.success(`Propiedad ${newStatus === 'APPROVED' ? 'aprobada' : 'rechazada'}`);
      fetchProperties();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-2">
              Panel de Administración
            </h1>
            <p className="text-slate-600">Gestiona las publicaciones de propiedades</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/admin/payment-plans')}
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
                data-testid="manage-plans-button"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Configurar Planes de Pago
              </Button>
              <Button
                onClick={() => navigate('/admin/coupons')}
                variant="outline"
                className="border-secondary text-secondary hover:bg-secondary hover:text-white"
                data-testid="manage-coupons-button"
              >
                <Ticket className="w-4 h-4 mr-2" />
                Gestionar Cupones
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-8">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-slate-700">Filtrar por estado:</label>
              <Select value={statusFilter || 'ALL'} onValueChange={(value) => setStatusFilter(value === 'ALL' ? '' : value)}>
                <SelectTrigger className="w-48" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="PENDING">Pendientes</SelectItem>
                  <SelectItem value="APPROVED">Aprobados</SelectItem>
                  <SelectItem value="REJECTED">Rechazados</SelectItem>
                  <SelectItem value="DRAFT">Borradores</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Properties List */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-slate-200 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : properties.length > 0 ? (
            <div className="space-y-4" data-testid="admin-properties-list">
              {properties.map((property) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`admin-property-${property.id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="md:w-64 h-48 md:h-auto flex-shrink-0">
                      <img
                        src={property.images?.[0] || 'https://images.unsplash.com/photo-1706808849827-7366c098b317?crop=entropy&cs=srgb&fm=jpg&q=85'}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Badge className={getStatusColor(property.status)}>
                              {getStatusLabel(property.status)}
                            </Badge>
                            <Badge variant="outline">
                              {getOperationLabel(property.operation)}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-heading font-bold text-slate-900 mb-1">
                            {property.title}
                          </h3>
                          <p className="text-slate-600 line-clamp-2">
                            {property.description}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-2xl font-heading font-bold text-primary">
                            {formatPrice(property.price)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                        <div className="text-sm text-slate-500">
                          <p>{property.city}, {property.department}</p>
                          <p>ID: {property.id.substring(0, 8)}</p>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/propiedad/${property.id}`)}
                            data-testid={`view-${property.id}`}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Ver
                          </Button>
                          {property.status === 'PENDING' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-secondary hover:bg-secondary-hover text-white"
                                onClick={() => handleStatusChange(property.id, 'APPROVED')}
                                data-testid={`approve-${property.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Aprobar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleStatusChange(property.id, 'REJECTED')}
                                data-testid={`reject-${property.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Rechazar
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="no-properties">
              <p className="text-xl text-slate-600">No hay propiedades con este estado</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPage;